import { useEffect, useRef, useState } from 'react';
import type { IUser } from '@models/IUser';
import type { IFieldElem } from '@models/IFormData';
import type { IDocumentData } from '@models/document/IDocumentData';
import type { IDocumentScheme } from '@models/document/IDocumentScheme';
import { convertListToMap } from '@utils/documentUtils';
import { computeVariablesField } from '@organisms/document/computeVariablesField';

interface IUseDocumentModalFieldsParams {
    data?: IDocumentData;
    scheme?: IDocumentScheme;
    user: IUser;
    isEdit: boolean;
    isNew: boolean;
}

interface IFieldsState {
    data: IDocumentData;
    scheme: IDocumentScheme;
    fields: Record<string, IFieldElem>;
}

/** Собирает runtime-карту полей модалки и отбрасывает поздний variables-ответ прошлого документа. */
export const useDocumentModalFields = ({ data, scheme, user, isEdit, isNew }: IUseDocumentModalFieldsParams) => {
    const [state, setState] = useState<IFieldsState>();
    const initVersion = useRef(0);

    useEffect(() => {
        const currentVersion = ++initVersion.current;
        setState(undefined);

        if (!data || !scheme) return;

        const init = async () => {
            const fields = convertListToMap(data.fields);
            fields.doc_Creator = {
                name: 'doc_Creator',
                value: data.doc_Creator,
                index: -1,
            };

            if (scheme.form.view.variables) {
                const variables = await computeVariablesField({
                    variables: scheme.form.view.variables,
                    docId: data.id,
                    user,
                    fields,
                    isEdit,
                    isNew,
                    isCurrent: () => initVersion.current === currentVersion,
                    source: 'useDocumentModalFields',
                });
                if (variables) fields.variables = variables;
            }

            if (initVersion.current === currentVersion) {
                setState({ data, scheme, fields });
            }
        };

        void init();
        return () => {
            if (initVersion.current === currentVersion) initVersion.current += 1;
        };
    }, [data, isEdit, isNew, scheme, user]);

    return state && state.data === data && state.scheme === scheme ? state.fields : undefined;
};
